import React, { FC, useEffect, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '@components/Icon';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useDispatch, useSelector } from 'react-redux';
import { BasketPaymentDetailsProps } from '@components/v2/BasketPaymentDetails';
import { useGetNotEscapedTranslationMarkup } from '@hooks/useGetNotEscapedTranslationMarkup';
import BasketSelectSection from '@components/v2/BasketSelectSection';
import { usePrices } from '@hooks/usePriceCalculator';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import { ICalculateSummaryRequestData } from '../../../services/calculateSummary/calculateSummary.types';
import { useFinanceWidgetDuck } from '@hooks/useFinanceWidgetDuck';
import { useSummaryDataPromise } from '@components/OfferListItem/hooks/useSummaryDataPromise';
import FinanceWidget from '@components/FinanceWidget';
import { faqsTemplateId } from '@components/FAQs';
import { BUDGET_TYPE_CASH, BUDGET_TYPE_FINANCE } from '../../../services';
import PriceSuffix from '@components/PriceSuffix';
import { getFinanceDetailText, getFinanceDetailView, getMonthlyPriceObjByType } from '@utils/Price.utils';
import { StockFormatedPriceType } from '../../../services/stock/models/stockItem.price.model';
import FilterDuck from '../../../redux/filters/filter.duck';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import Spinner from '@components/Spinner';
import { theme } from '../../../styles/theme';
import {
    BASKET_STEPS,
    FINANCE_DETAIL_VIEW,
    isBrandAC,
    isBrandAP,
    isBrandDS,
    isMarketGB,
    ModalVersion,
} from '../../../constants/main';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import IconButton from '@components/IconButton';
import { useGetPaymentKeywords } from '@components/OfferListItem/utils/getOfferKeywords';
import { useJourneyType } from '@hooks/useJourneyType';
import { updateBasketSteps } from '@utils/Car.utils';
import { Link } from '@components/v2/link';
import MonthlyPriceLabel, { AprSizes } from '@components/MonthlyPriceLabel';
import { IconTemplate } from '@components/Icon/IconTemplate';
import NeedHandSection from '@components/OfferListItem/NeedHandSection';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import { PaymentJourneyTypes } from '../../../partExchange/interfaces/Default';

const BasketPaymentDetailsTemplate: FC<BasketPaymentDetailsProps> = ({ className, generatedIndex }) => {
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const { carDetails, currentDeal, promoCodeInDeal } = useCarDetailsDuck();
    const { financeQuote, pricesList, globalTexts } = useFinanceWidgetDuck();
    const { formatMoney } = useMoneyFormatter();
    const { isStockJourney } = useCarStockJourneyCheck();

    const [renderWidget, setRenderWidget] = useState<() => void>();
    const isLoading = useSelector((state) => !!UIDuck.getOwnState(state).loading);

    const { cash, finance } = usePrices();

    const { getNotEscapedTranslationMarkup } = useGetNotEscapedTranslationMarkup();

    const [isOpen, setIsOpen] = useState(true);

    const handleFinanceWidgetBtnClick = () => {
        renderWidget && renderWidget();
    };

    const registerRenderCb = (cb: () => void) => {
        setRenderWidget(() => cb);
    };

    const { totalPrice: cashPrice } = cash;
    const {
        totalPrice: monthlyPrice,
        financeProperties: { deposit, duration, annualMileage, apr, tin, finalPayment },
    } = finance;

    const params: ICalculateSummaryRequestData = {
        carConfigId: carDetails._id ?? carDetails.externalId,
        token: financeQuote?.token ?? currentDeal?.financeQuoteId,
    };

    const isFinanceWidgetDisabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_DISABLED_FINANCE_WIDGET);
    const isAutomaticOpeningFinanceWidgetEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_AUTOMATIC_OPENING_FINANCE_WIDGET_ON_BASKET_PAGE_ENABLED
    );
    const showPaymentDeposit = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SHOW_PAYMENT_DEPOSIT);
    const { processCalculateSummaryDataPromise } = useSummaryDataPromise(params);

    const showAnnualMileage = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SHOW_ANNUAL_MILEAGE);
    const showTanAndTaeg = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SHOW_TAN_AND_TAEG);

    const paymentKeywords = useGetPaymentKeywords(deposit, duration, annualMileage, apr);

    const promoText =
        financeQuote?.primaryText ?? (carDetails?.extraFields?.promotionalText || carDetails?.promotionalText);

    const scrollToFAQs = () => {
        const element = document.getElementById(faqsTemplateId);

        return element.scrollIntoView();
    };

    const { paymentJourneyType } = useJourneyType();
    const { hasSelectedPaymentJourney = false } = currentDeal?.extraFields ?? {};
    const hasSelectedFinance = hasSelectedPaymentJourney && paymentJourneyType === BUDGET_TYPE_FINANCE;
    const hasSelectedCash = hasSelectedPaymentJourney && paymentJourneyType === BUDGET_TYPE_CASH;
    const selectPaymentMethod = async (selectedPaymentMethod: string) => {
        const previousPaymentJourney = paymentJourneyType;
        const hasPreviousSelectedJourney = hasSelectedPaymentJourney;

        if (!hasSelectedPaymentJourney && paymentJourneyType === selectedPaymentMethod) {
            dispatch(UIDuck.setLoading(5));
            dispatch(FilterDuck.changeBudgetType(selectedPaymentMethod));

            // update deal that we infact selected
            await updateBasketSteps(dispatch, currentDeal, BASKET_STEPS.PAYMENT, selectedPaymentMethod);
            dispatch(UIDuck.setLoading(-5));
        } else {
            dispatch(FilterDuck.changeBudgetType(selectedPaymentMethod));
        }

        if (
            isAutomaticOpeningFinanceWidgetEnabled &&
            selectedPaymentMethod === PaymentJourneyTypes.FINANCE &&
            (!hasPreviousSelectedJourney || previousPaymentJourney === PaymentJourneyTypes.CASH)
        ) {
            handleFinanceWidgetBtnClick();
        }
    };

    useEffect(() => {
        if (!monthlyPrice) {
            selectPaymentMethod(BUDGET_TYPE_CASH);
        }
    }, []);

    const amountRounding = getMonthlyPriceObjByType(carDetails.prices, StockFormatedPriceType.EMPLOYEE)?.amountRounding;

    const fee =
        promoCodeInDeal && JSON.parse(promoCodeInDeal)?.depositAmount
            ? JSON.parse(promoCodeInDeal)?.depositAmount
            : currentDeal?.extraFields?.reservationFee;

    const financeDetailView = getFinanceDetailView(carDetails.prices, StockFormatedPriceType.EMPLOYEE);
    const financeDetailText = getFinanceDetailText(
        carDetails.prices,
        StockFormatedPriceType.EMPLOYEE,
        globalTexts,
        pricesList,
        carDetails.externalId || carDetails.vehicleId
    );

    return (
        <div id="payments" className={className}>
            <Collapsible
                lazyRender={true}
                open={isOpen}
                trigger={
                    <AccessibleButton className="titleWrapperButton" onClick={() => setIsOpen(!isOpen)}>
                        <div className="titleWrapper">
                            <div className="titleContainer">
                                <span className="sectionNumber">{generatedIndex}.</span>
                                <span className="title">{`${t('basket.payment.title').toLowerCase()}`}</span>
                            </div>
                            <Icon
                                className={isOpen ? 'isOpen' : 'isClose'}
                                name={isOpen ? Icons.ChevronUp : Icons.ChevronDown}
                            />
                        </div>
                    </AccessibleButton>
                }
            >
                <div className="description">
                    <span>{t('basket.payment.description')}</span>
                    <Link
                        withParent={false}
                        isUnderline
                        txt={t('basket.payment.description.link')}
                        onClick={scrollToFAQs}
                    >
                        <button className="link" onClick={scrollToFAQs}>
                            {t('basket.payment.description.link')}
                        </button>
                    </Link>
                </div>
                <BasketSelectSection
                    leftSectionTitle={t('basket.payment.cash.title')}
                    leftSectionDescription={t('basket.payment.cash.description')}
                    rightSectionTitle={t('basket.payment.finance.title')}
                    rightSectionDescription={t('basket.payment.finance.description')}
                    leftValue={BUDGET_TYPE_CASH}
                    rightValue={monthlyPrice && BUDGET_TYPE_FINANCE}
                    leftSelected={
                        (hasSelectedPaymentJourney || !monthlyPrice) && paymentJourneyType === BUDGET_TYPE_CASH
                    }
                    rightSelected={hasSelectedPaymentJourney && paymentJourneyType === BUDGET_TYPE_FINANCE}
                    onClick={selectPaymentMethod}
                />
                {isLoading && <Spinner size={25} border={3} color={theme.colors.black} />}
                {!isLoading && hasSelectedFinance && (
                    <div className="financeSummary">
                        <div className="monthlyPaymentDesktop">
                            <span className="financeTitle">{t('basket.orderSummary.orderMonthly')}</span>
                            <span className="price">
                                {formatMoney(monthlyPrice, false, '', amountRounding)}
                                <PriceSuffix checkPrioritizedTranslation isMonthlyPrice />
                                {financeDetailView === FINANCE_DETAIL_VIEW.ICON && (
                                    <IconButton
                                        className="infoIcon"
                                        icon={isBrandAC || isBrandAP || isBrandDS ? Icons.InfoCircle : Icons.Info}
                                        width={16}
                                        height={16}
                                        onClick={processCalculateSummaryDataPromise}
                                    />
                                )}
                            </span>
                        </div>
                        <div className="monthlyPaymentMobile">
                            <span className="price">{t('basket.payment.selectedFinance.title')}</span>
                            <span>{t('basket.payment.selectedFinance.description')}</span>
                        </div>
                        <div className="monthlyPaymentDetailDesktop">
                            <span>{t('basket.payment.selectedFinance.description')}</span>
                            {financeDetailView === FINANCE_DETAIL_VIEW.LINK && (
                                <MonthlyPriceLabel
                                    withPrimaryColor={isMarketGB}
                                    withoutMarginTop
                                    alignToRight
                                    aprValue={apr}
                                    aprCustomTextKey={promoText}
                                    size={AprSizes.MEDIUM}
                                    calculateSummaryData={processCalculateSummaryDataPromise}
                                    financeDetailText={financeDetailText}
                                />
                            )}
                        </div>
                        <div className="monthlyPaymentDetailMobile">
                            <span className="price">
                                {formatMoney(monthlyPrice, false, '', amountRounding)}
                                <PriceSuffix isMonthlyPrice />
                                {financeDetailView === FINANCE_DETAIL_VIEW.ICON && (
                                    <IconButton
                                        className="infoIcon"
                                        icon={isBrandAC || isBrandAP || isBrandDS ? Icons.InfoCircle : Icons.Info}
                                        width={16}
                                        height={16}
                                        onClick={processCalculateSummaryDataPromise}
                                    />
                                )}
                                {financeDetailView === FINANCE_DETAIL_VIEW.LINK && (
                                    <MonthlyPriceLabel
                                        withPrimaryColor={isMarketGB}
                                        aprValue={apr}
                                        aprCustomTextKey={promoText}
                                        size={AprSizes.MEDIUM}
                                        calculateSummaryData={processCalculateSummaryDataPromise}
                                        financeDetailText={financeDetailText}
                                    />
                                )}
                            </span>
                        </div>
                        <div className="monthlyPaymentSummary">
                            <div className="monthlyPaymentItem">
                                <span>{t('finance.firstRental')}</span>
                                <span className="price">{formatMoney(deposit)}</span>
                            </div>
                            <div className="monthlyPaymentItem">
                                <span>{t('finance.duration')}</span>
                                <span>
                                    {duration} {t('finance.duration.unit')}
                                </span>
                            </div>
                            {showAnnualMileage && (
                                <div className="monthlyPaymentItem">
                                    <span>{t('finance.annualMileage')}</span>
                                    <span>
                                        {annualMileage} {t('finance.annualMileage.units')}
                                    </span>
                                </div>
                            )}
                            {showTanAndTaeg && (
                                <>
                                    <div className="monthlyPaymentItem">
                                        <span>{t('finance.tan')}</span>
                                        <span>{tin?.toString()?.replace('.', ',')}%</span>
                                    </div>
                                    <div className="monthlyPaymentItem">
                                        <span>{t('finance.taeg')}</span>
                                        <span>{apr?.toString()?.replace('.', ',')}%</span>
                                    </div>
                                    <div className="monthlyPaymentItem">
                                        <span>{t('finance.balloon')}</span>
                                        {finalPayment && finalPayment > 0 && <span>{formatMoney(finalPayment)}</span>}
                                    </div>
                                </>
                            )}
                        </div>
                        <button onClick={handleFinanceWidgetBtnClick} className="personalizeButton">
                            <span>{t('basket.payment.selectedFinance.personalize')}</span>
                        </button>
                    </div>
                )}
                {!isLoading && (hasSelectedCash || !monthlyPrice) && (
                    <div className="cashSummary">
                        <div className="leftSection">
                            <p
                                dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                                    'basket.payment.selectedCash.description1'
                                )}
                            />
                            <p
                                dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                                    'basket.payment.selectedCash.description2'
                                )}
                            />
                        </div>
                        <div className="rightSection">
                            {showPaymentDeposit && fee > 0 && (
                                <p>
                                    <span>{t('basket.payment.selectedCash.deposit')}</span>
                                    <span className="price">{formatMoney(fee, true)}</span>
                                </p>
                            )}
                            {showPaymentDeposit && fee > 0 && (
                                <p>
                                    <span>{t('basket.payment.selectedCash.remainingAmount')}</span>
                                    <span className="price">{formatMoney(cashPrice - fee)}</span>
                                </p>
                            )}
                            <p className="totalPrice">
                                <span>{t('offerDetail.totalPrice.title')}</span>
                                <span className="price">
                                    {formatMoney(cashPrice)}{' '}
                                    <PriceSuffix checkPrioritizedTranslation showOnlyPrioritizedTranslation />
                                </span>
                            </p>
                        </div>
                    </div>
                )}
                {((hasSelectedCash || !monthlyPrice) || hasSelectedFinance) && (
                    <div className="legalText">
                        {(hasSelectedCash || !monthlyPrice) && (
                            <p>
                                <span
                                    dangerouslySetInnerHTML={getNotEscapedTranslationMarkup('cash.legal.text.long')}
                                />
                            </p>
                        )}
                        {hasSelectedFinance &&
                            (isMarketGB ? (
                                <p>{paymentKeywords}</p>
                            ) : (
                                <p>
                                    <span
                                        dangerouslySetInnerHTML={getNotEscapedTranslationMarkup('finance.legal.text1')}
                                    />
                                    <br />
                                    <span
                                        dangerouslySetInnerHTML={getNotEscapedTranslationMarkup('finance.legal.text2')}
                                    />
                                    <br />
                                    <span
                                        dangerouslySetInnerHTML={getNotEscapedTranslationMarkup('finance.legal.text3')}
                                    />
                                </p>
                            ))}
                    </div>
                )}
                <NeedHandSection isSidebar isBasket isInlineSnippet />
            </Collapsible>
            {!isFinanceWidgetDisabled && <FinanceWidget registerRenderCb={registerRenderCb} />}
        </div>
    );
};

export default BasketPaymentDetailsTemplate;
